<template>
  <div class="modal-content">
    <community-form
      :community="community"
      :edit-type="true"
      @changeImage="onChangeImage"
    />
    <div class="modal-footer">
      <div class="messg">
        <div class="savingMessage">
          {{ savingMessage }}
        </div>
        <div class="errorMessage">
          {{ errorMessage }}
        </div>
      </div>
      <v-button
        id="create-community-button"
        data-cy="submit-save-community"
        :border-radius="'50px'"
        :is-loading="loading"
        :on-click="create"
        class="create-button"
        :disabled="isSubmitDisabled"
      >
        <span>{{ $t('community.createCommunityModal.create') }}</span>
      </v-button>
    </div>
  </div>
</template>

<script>
import VButton from '../shared/VButton'
import CommunityForm from '@/components/community/CommunityForm'
import FormUtils from '../../form-utils'

export default {
  name: 'CreateCommunityModal',
  components: { CommunityForm, VButton },
  props: {
    onClose: {
      type: Function,
      required: true
    },
    community: {
      type: Object,
      required: false,
      default: function () {
        return {
          name: '',
          description: '',
          type: 'public',
          isBroadcast: false
        }
      }
    }
  },
  data() {
    return {
      savingMessage: '',
      errorMessage: '',
      image: '',
      loading: false
    }
  },
  methods: {
    async create() {
      if (!await this.isFormValid()) {
        return
      }
      this.errorMessage = ''
      this.savingMessage = this.$t('community.createCommunityModal.saving')
      await this.saveCommunity()
    },
    async saveCommunity() {
      this.loading = true
      let formData = new FormData()
      FormUtils.append(formData, 'img', this.image)
      FormUtils.appendObject(formData, this.community)
      const responseCommunity = await this.$store.dispatch('createCommunity', formData)
      if (responseCommunity.status === 201) {
        await this.$store.dispatch('addCommunity', responseCommunity.data)
        this.savingMessage = ''
        this.onClose()
        this.$router.push({ name: 'communityPage', params: { id: responseCommunity.data.id } })
      } else {
        this.showErrorMessage()
      }
      this.loading = false
    },
    showErrorMessage() {
      this.savingMessage = ''
      this.errorMessage = this.$t('community.createCommunityModal.somethingWentWrong')
    },
    async isFormValid() {
      const result = await this.$validator.validateAll()
      return !!result
    },
    onChangeImage(image) {
      this.image = image
    },
    isValidImage() {
      // uploaded image should be less than 10 Mo
      if (typeof this.image === 'string' && this.image !== '') {
        const encodedImage = Buffer.from(this.image, 'base64')
        return encodedImage.length > 0 && encodedImage.length <= 10000000
      }
      return true
    }
  },
  computed: {
    isSubmitDisabled() {
      const isValidImage = this.isValidImage()
      return !this.community.name || !this.community.description || !isValidImage
    }
  }
}
</script>

<style scoped lang="scss">
.modal-content {
  width: 912px;

  @media (max-width: $header-change) {
      width:100vw;
  }

  .form {
    display: flex;
    align-items: stretch;

    @media (max-width: $header-change) {
          flex-wrap: wrap;
          justify-content: center;
          overflow-y:scroll;
          height: calc(100vh - 150px) !important;
    }

    .help-info {
      font-size: 13px;
      text-align: left;

      &.left {
        margin-left: 30px;
      }

      &.right {
        margin-left: 20px;
      }
    }

    .left-col {
      display: flex;
      flex-direction: column;
      width: 426px;
      align-items: flex-start;
      padding: 24px 24px 0 24px;

      .input-block {
        margin-bottom: 31px;
        font-size: 13px;
        letter-spacing: 0;
        line-height: 19px;
        color: $black-color;
        display: flex;
        flex-direction: column;
        align-items: flex-start;

        .under-field-container {
          height: 1.2rem;

          .validate-message {
            color: $cerise-color;
            font-weight: bold;
            display: inline;
          }

          span {
            display: inline;
          }
        }

        .input {
          background: #FFFFFF;
          border: 1px solid #D8D8D8;
          border-radius: 3px;
          min-height: 31px;

          &.error {
            border: 1px solid $cerise-color;
          }
        }

        input {
          padding: 0 10px;
          width: 404px;
        }

        textarea {
          height: 55px;
          max-width: 404px;
          width: 404px;
          padding: 10px 10px;
          font-family: OpenSans-Regular, sans-serif;
          font-size: 13px;
          resize: none;
        }
      }
    }

    .right-col {
      margin-left: 1.5rem;
      display: flex;
      flex-direction: column;
      align-items: flex-start;

      .checkbox {
        font-size: 15px;
        display: flex;
        align-items: flex-end;

        input:hover, label:hover {
          color: $primary-color;
          cursor: pointer;
        }
      }

      .image-select {
        margin: 1.5rem 0;
        width: 396px;
        height: 223px;
      }
    }
  }
}

.container {
  display: flex;
  flex-direction: column;
  width: 100%;

  &.public {
    margin-bottom: 20px;
  }

  &.private {
    margin-bottom: 5px;
  }

  /* Customize the label (the container) */
  .radio {
    display: flex;
    flex-direction: row;
    align-items: center;
    position: relative;
    padding-left: 28px;
    cursor: pointer;
    font-size: 15px;
    line-height: 22px;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;

    &:hover {
      .checkmark {
        border-color: $primary-color;
      }

      label {
        color: $primary-color;
        cursor: pointer;
      }
    }

    /* Hide the browser's default radio button */
    input {
      position: absolute;
      opacity: 0;
      cursor: pointer;
      height: 0;
      width: 0;
      /* When the radio button is checked, add a blue background */
      &:checked ~ .checkmark {
        background-color: white;
        border: 5px solid $primary-color;
        border-radius: 50%;
        width: 10px;
        height: 10px;
      }

      /* Show the indicator when checked */
      & ~ .checkmark:after {
        display: block;
      }
    }

    .checkmark {
      position: absolute;
      top: 1px;
      left: 1px;
      background-color: white;
      border: 1px solid lightgray;
      border-radius: 50%;
      width: 18px;
      height: 18px;
    }
  }
}

.left-col {
  display: flex;
  flex-direction: column;
  width: 426px;
  align-items: flex-start;
  padding: 24px;

  .input-block {
    margin-bottom: 31px;
    font-size: 13px;
    letter-spacing: 0;
    line-height: 19px;
    color: $black-color;
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    .under-field-container {
      height: 1.2rem;

      .validate-message {
        color: $cerise-color;
        font-weight: bold;
        display: inline;
      }

      span {
        display: inline;
      }
    }

    .input {
      background: #FFFFFF;
      border: 1px solid #D8D8D8;
      border-radius: 3px;
      min-height: 31px;

      &.error {
        border: 1px solid $cerise-color;
      }
    }

    input {
      padding: 0 10px;
      width: 404px;
    }

    textarea {
      height: 55px;
      max-width: 404px;
      width: 404px;
      padding: 10px 10px;
      font-family: OpenSans-Regular, sans-serif;
      font-size: 13px;
      resize: none;
    }
  }
}

.modal-footer {
  box-shadow: inset 0 1px 0 0 #D8D8D8;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 60px;

  .messg {
    display: flex;

    .savingMessage {
      margin: 24px 0 24px 24px;
      color: $primary-color;
    }

    .errorMessage {
      margin: 24px 0 24px 0;
      color: $cerise-color;
    }
  }

  .create-button {
    margin: 22px 24px;
    min-width: 79px;
  }
}
</style>
